/**
 * Browser Notifications Service
 * Sends native browser notifications when the page is not visible
 */

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationOptions {
  title: string;
  message: string;
  type: NotificationType;
  tag?: string;
  onClick?: () => void;
}

class BrowserNotificationsService {
  private _permission: NotificationPermission = 'default';
  private _supported: boolean = false;

  constructor() {
    this._supported = 'Notification' in window;
    if (this._supported) {
      this._permission = Notification.permission;
    }
  }

  get isSupported(): boolean {
    return this._supported;
  }

  get permission(): NotificationPermission {
    return this._permission;
  }

  get isEnabled(): boolean {
    return this._supported && this._permission === 'granted';
  }

  /**
   * Request permission for browser notifications
   * Returns true if permission was granted
   */
  async requestPermission(): Promise<boolean> {
    if (!this._supported) {
      return false;
    }

    if (this._permission === 'granted') {
      return true;
    }

    if (this._permission === 'denied') {
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      this._permission = result;
      return result === 'granted';
    } catch {
      return false;
    }
  }

  /**
   * Check if the page is currently visible
   */
  private isPageVisible(): boolean {
    return document.visibilityState === 'visible' && document.hasFocus();
  }

  /**
   * Get icon path based on notification type
   */
  private getIcon(_type: NotificationType): string {
    // Use the app favicon for notifications
    return '/favicon.svg';
  }

  /**
   * Send a browser notification if page is not visible
   * @param options Notification options
   * @param forceShow Force show even if page is visible (default: false)
   */
  notify(options: NotificationOptions, forceShow: boolean = false): Notification | null {
    // Don't show if page is visible (unless forced)
    if (!forceShow && this.isPageVisible()) {
      return null;
    }

    // Don't show if not enabled
    if (!this.isEnabled) {
      return null;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.message,
        icon: this.getIcon(options.type),
        tag: options.tag, // Prevents duplicate notifications with same tag
        requireInteraction: options.type === 'error', // Errors stay until dismissed
        silent: false
      });

      if (options.onClick) {
        notification.onclick = () => {
          window.focus();
          options.onClick?.();
          notification.close();
        };
      } else {
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }

      // Auto-close non-error notifications after 5 seconds
      if (options.type !== 'error') {
        setTimeout(() => notification.close(), 5000);
      }

      return notification;
    } catch {
      return null;
    }
  }

  /**
   * Convenience methods for different notification types
   */
  success(title: string, message: string, tag?: string): Notification | null {
    return this.notify({ title, message, type: 'success', tag });
  }

  error(title: string, message: string, tag?: string): Notification | null {
    return this.notify({ title, message, type: 'error', tag });
  }

  info(title: string, message: string, tag?: string): Notification | null {
    return this.notify({ title, message, type: 'info', tag });
  }

  warning(title: string, message: string, tag?: string): Notification | null {
    return this.notify({ title, message, type: 'warning', tag });
  }
}

// Export singleton instance
export const browserNotifications = new BrowserNotificationsService();
export default browserNotifications;
